import { Box3, Vector3, type Material, type Object3D, type SkinnedMesh, type Texture } from "three";
import type { Model3dMeta } from "@agentsaas/protocol";

/**
 * 3D 查看器的纯逻辑（不依赖 WebGL，便于测试）：
 * - 格式识别：按 mediaType 或扩展名选 GLTF / FBX / OBJ 加载器
 * - 状态机：loading → ready | error；动画片段 / 播放 / 速度；显示开关
 * - 现场计算模型指标（资产没有 AssetVersion.model 时用）
 * - 视角同步（版本对比时两个查看器联动）
 */

export type ModelFormat = "gltf" | "fbx" | "obj";

const EXT: Record<string, ModelFormat> = { glb: "gltf", gltf: "gltf", fbx: "fbx", obj: "obj" };
const MEDIA: Record<string, ModelFormat> = {
  "model/gltf-binary": "gltf",
  "model/gltf+json": "gltf",
  "model/fbx": "fbx",
  "model/vnd.fbx": "fbx",
  "application/fbx": "fbx",
  "model/obj": "obj",
  "text/x-obj": "obj",
};

/** mediaType 优先，其次文件名 / 地址的扩展名；都认不出时返回 null */
export function modelFormat(mediaType?: string | null, name?: string | null): ModelFormat | null {
  const m = mediaType ? MEDIA[mediaType.split(";")[0]!.trim().toLowerCase()] : undefined;
  if (m) return m;
  const ext = name ? /\.([a-z0-9]+)(?:[?#].*)?$/i.exec(name)?.[1]?.toLowerCase() : undefined;
  return ext ? (EXT[ext] ?? null) : null;
}

/** 文件名是否是查看器能打开的模型 */
export const isModelFile = (name: string) => modelFormat(null, name) !== null;

export const SPEEDS = [0.25, 0.5, 1, 1.5, 2] as const;

export interface Display {
  wireframe: boolean;
  skeleton: boolean;
  grid: boolean;
  env: boolean;
}

export interface ViewerState {
  status: "loading" | "ready" | "error";
  meshes: number;
  clips: string[];
  clip: string | null;
  playing: boolean;
  speed: number;
  display: Display;
  showStats: boolean;
  spin: boolean;
  /** 现场计算的指标（加载完成后） */
  computed: Model3dMeta | null;
}

export type ViewerAction =
  | { type: "load" }
  | { type: "loaded"; clips: string[]; stats: Model3dMeta }
  | { type: "failed" }
  | { type: "clip"; clip: string }
  | { type: "toggle_play" }
  | { type: "speed"; speed: number }
  | { type: "display"; key: keyof Display }
  | { type: "stats" }
  | { type: "spin" };

export const initialViewer = (opts: { showStats?: boolean } = {}): ViewerState => ({
  status: "loading",
  meshes: 0,
  clips: [],
  clip: null,
  playing: false,
  speed: 1,
  display: { wireframe: false, skeleton: false, grid: true, env: true },
  showStats: opts.showStats ?? false,
  spin: false,
  computed: null,
});

export function viewerReducer(s: ViewerState, a: ViewerAction): ViewerState {
  switch (a.type) {
    case "load":
      return { ...initialViewer({ showStats: s.showStats }), display: s.display, speed: s.speed };
    case "loaded":
      // 有动画时默认播放第一个片段
      return { ...s, status: "ready", meshes: a.stats.meshes, clips: a.clips, clip: a.clips[0] ?? null, playing: a.clips.length > 0, computed: a.stats };
    case "failed":
      return { ...s, status: "error" };
    case "clip":
      return s.clips.includes(a.clip) ? { ...s, clip: a.clip, playing: true } : s;
    case "toggle_play":
      return s.clip ? { ...s, playing: !s.playing } : s;
    case "speed":
      return (SPEEDS as readonly number[]).includes(a.speed) ? { ...s, speed: a.speed } : s;
    case "display":
      // 骨骼只对带蒙皮的模型有意义
      if (a.key === "skeleton" && !s.computed?.skinned) return s;
      return { ...s, display: { ...s.display, [a.key]: !s.display[a.key] } };
    case "stats":
      return { ...s, showStats: !s.showStats };
    case "spin":
      return { ...s, spin: !s.spin };
  }
}

const TEXTURE_SLOTS = ["map", "normalMap", "roughnessMap", "metalnessMap", "aoMap", "emissiveMap", "alphaMap", "bumpMap", "specularMap"] as const;

/** 现场计算指标：三角面、顶点、网格、材质、贴图、骨骼、包围盒（米，含节点变换） */
export function computeStats(root: Object3D, animations: { name: string }[], format: Model3dMeta["format"]): Model3dMeta {
  let tris = 0;
  let verts = 0;
  let meshes = 0;
  const materials = new Set<Material>();
  const textures = new Map<Texture, { width: number; height: number; mimeType: string }>();
  const bones = new Set<Object3D>();
  root.updateMatrixWorld(true);
  root.traverse((o) => {
    const m = o as SkinnedMesh;
    if (!m.isMesh) return;
    meshes++;
    const g = m.geometry;
    const pos = g.getAttribute("position");
    if (pos) verts += pos.count;
    tris += Math.floor((g.index ? g.index.count : (pos?.count ?? 0)) / 3);
    for (const mat of Array.isArray(m.material) ? m.material : [m.material]) {
      materials.add(mat);
      for (const slot of TEXTURE_SLOTS) {
        const tex = (mat as unknown as Record<string, Texture | null | undefined>)[slot];
        if (!tex?.isTexture || textures.has(tex)) continue;
        const img = tex.image as { width?: number; height?: number } | undefined;
        textures.set(tex, { width: img?.width ?? 0, height: img?.height ?? 0, mimeType: (tex.userData?.mimeType as string | undefined) ?? "image/unknown" });
      }
    }
    if (m.isSkinnedMesh) for (const b of m.skeleton.bones) bones.add(b);
  });
  const box = new Box3().setFromObject(root);
  const size = box.isEmpty() ? new Vector3() : box.getSize(new Vector3());
  const r = (n: number) => Math.round(n * 1000) / 1000;
  return {
    format,
    tris,
    verts,
    meshes,
    materials: materials.size,
    textures: [...textures.values()],
    animations: animations.map((a) => a.name),
    skinned: bones.size > 0,
    joints: bones.size,
    bbox: { x: r(size.x), y: r(size.y), z: r(size.z) },
    validation: null,
  };
}

// ───────────── 视角同步 ─────────────

export interface ViewPose {
  position: [number, number, number];
  target: [number, number, number];
}

export interface ViewSync {
  enabled: boolean;
  publish(from: symbol, pose: ViewPose): void;
  subscribe(id: symbol, cb: (pose: ViewPose) => void): () => void;
}

/** 多个查看器之间的视角联动：某个查看器的相机变化广播给其他查看器（不回给自己）；enabled=false 时不广播 */
export function createViewSync(): ViewSync {
  const subs = new Map<symbol, (pose: ViewPose) => void>();
  const sync: ViewSync = {
    enabled: true,
    publish(from, pose) {
      if (!sync.enabled) return;
      for (const [id, cb] of subs) if (id !== from) cb(pose);
    },
    subscribe(id, cb) {
      subs.set(id, cb);
      return () => subs.delete(id);
    },
  };
  return sync;
}

/** 截图文件名：名字里的非法字符换成下划线 */
export function screenshotName(name: string): string {
  return `${name.replace(/[\\/:*?"<>|\s]+/g, "_").replace(/^_+|_+$/g, "") || "model"}.png`;
}
